import Button from "@edaz/edazkit/Button/index";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import type ChargeOrderSearchResponse from "entities/chargeOrderSearchResponse";
import toLower from "lodash/toLower";
import { observer } from "mobx-react-lite";
import React from "react";
import { ButtonToolbar } from "react-bootstrap";
import { formatAmount, formatDate, formatPhoneNumber } from "utils/localizations";
import PairingByOrderListViewModel from "viewModels/finance/paymentsPairing/pairingByOrder/pairingByOrderListViewModel";
import type PairingByPaymentOrdersViewModel from "viewModels/finance/paymentsPairing/pairingByPayment/pairingByPaymentOrdersViewModel";
import type { ListColumnDefinition } from "views/common/list";
import ListView from "views/common/list";
import ChargeOrderStatus from "entities/chargeOrderStatus";
import SourceType from "entities/sourceType";
import OrderFilters from "../orderFilters";
import { emptyValue } from "utils/emptyValue";

// tslint:disable-next-line:cognitive-complexity
export function ChargeOrderSearchColumnsDefinition(
  vm: PairingByOrderListViewModel | PairingByPaymentOrdersViewModel
): ListColumnDefinition<ChargeOrderSearchResponse>[] {
  return [
    {
      title: vm.translateSubAttribute("charge", "vehicle"),
      valueFormatter: props => {
        if (props.item.charge?.vehicle) {
          return `${vm.countryCode(props.item.charge.vehicle.countryId)} - ${props.item.charge.vehicle.licensePlate}`;
        } else {
          return vm.translate("vehicles", props.item.chargesCount).replace("%count%", props.item.chargesCount.toString());
        }
      },
      cellClassName: "font-weight-bold",
    },
    {
      property: "status",
      title: vm.translateAttribute("status"),
      valueFormatter: props => vm.translateState(props.value),
    },
    {
      property: "orderIdent",
      title: vm.translateAttribute("orderIdent"),
      numberProperty: true,
      valueFormatter: props => props.value || emptyValue,
    },
    {
      property: "createdAt",
      title: vm.translateAttribute("createdAt"),
      valueFormatter: props => formatDate(props.value),
    },
    {
      property: "chargesCount",
      title: vm.translateSubAttribute("charge", "chargeTypeId"),
      valueFormatter: props => vm.translateChargeType(props.item.charge?.priceListItemId),
    },
    {
      property: "amount",
      title: vm.translateAttribute("amount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      title: vm.translateSubAttribute("charge", "validSince"),
      dateProperty: true,
      valueFormatter: props => (props.item.charge ? formatDate(props.item.charge.validSince) : emptyValue),
    },
    {
      title: vm.translateSubAttribute("charge", "validUntil"),
      dateProperty: true,
      valueFormatter: props => (props.item.charge ? formatDate(props.item.charge.validUntil) : emptyValue),
    },
    {
      property: "sourceType",
      title: vm.translateAttribute("sourceType"),
      valueFormatter: props => {
        if (!props.value) {
          return emptyValue;
        } else {
          const sourceName = vm.translateSourceType(props.value);
          let paymentType = "";
          if (props.value === SourceType.Eshop && props.item.paymentType) {
            paymentType = ` (${toLower(vm.translatePaymentType(props.item.paymentType))})`;
          }

          return `${sourceName}${paymentType}`;
        }
      },
    },
    {
      title: vm.translateSubAttribute("charge", "emailAddress"),
      valueFormatter: props => (props.item.charge ? props.item.charge.email : emptyValue),
    },
    {
      title: vm.translateSubAttribute("charge", "phoneNumber"),
      valueFormatter: props => (props.item.charge?.phoneNumber ? formatPhoneNumber(props.item.charge.phoneNumber) : emptyValue),
    },
  ];
}

export const PairingByOrderListView: ViewComponent<PairingByOrderListViewModel> = observer(({ vm }) => {
  const onItemClick = vm.onItemClick();
  const columns: ColumnDefinition<ChargeOrderSearchResponse>[] = [
    ...ChargeOrderSearchColumnsDefinition(vm),
    {
      cellClassName: "sticky",
      headerClassName: "sticky",
      valueFormatter: props => (
        <ButtonToolbar className="justify-content-end">
          <Button
            className="rounded"
            variant="success"
            size="sm"
            onClick={onItemClick ? () => onItemClick(props.item) : undefined}>
            {vm.translate(props.item.status !== ChargeOrderStatus.Paid && vm.allowToPair ? "match" : "orderDetail")}
          </Button>
        </ButtonToolbar>
      ),
    },
  ];

  return (
    <>
      {!vm.activeChild && <OrderFilters vm={vm} />}
      <ListView vm={vm} columns={columns} hideTitle={true} hideButtons={true} mandatoryFilter={true} />
    </>
  );
});

registerView(PairingByOrderListView, PairingByOrderListViewModel);
